import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Apple, 
  Lock, 
  CheckCircle2, 
  ChevronRight, 
  Loader2,
  Trophy,
  Calculator,
  Utensils,
  RefreshCw,
  Flame,
  Target,
  Scale,
  Coffee,
  Sun,
  Cookie,
  Moon,
  Sparkles,
  ArrowRight,
  Activity,
  Dumbbell
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Banco de comidas por tipo y rango calórico
const BANCO_COMIDAS = {
  desayuno: [
    { nombre: "Tortilla de 3 huevos + tostada integral", calorias: 350, proteina: 25, carbos: 25, grasa: 18, emoji: "🍳" },
    { nombre: "Yogur griego con avena y frutos rojos", calorias: 320, proteina: 20, carbos: 35, grasa: 12, emoji: "🥣" },
    { nombre: "Batido proteico con plátano y avena", calorias: 380, proteina: 30, carbos: 40, grasa: 10, emoji: "🥤" },
    { nombre: "Tostadas de aguacate con pavo", calorias: 340, proteina: 22, carbos: 28, grasa: 16, emoji: "🥑" },
    { nombre: "Gachas de avena con whey y nueces", calorias: 400, proteina: 28, carbos: 42, grasa: 14, emoji: "🥜" },
    { nombre: "Huevos revueltos con salmón ahumado", calorias: 360, proteina: 30, carbos: 5, grasa: 24, emoji: "🐟" },
  ],
  comida: [
    { nombre: "Pollo a la plancha con arroz y verduras", calorias: 550, proteina: 45, carbos: 55, grasa: 12, emoji: "🍗" },
    { nombre: "Salmón al horno con patata y brócoli", calorias: 520, proteina: 40, carbos: 40, grasa: 20, emoji: "🐟" },
    { nombre: "Ternera con quinoa y ensalada", calorias: 580, proteina: 48, carbos: 45, grasa: 18, emoji: "🥩" },
    { nombre: "Pasta integral con pavo y tomate", calorias: 560, proteina: 38, carbos: 65, grasa: 14, emoji: "🍝" },
    { nombre: "Lentejas con verduras y huevo", calorias: 480, proteina: 32, carbos: 55, grasa: 12, emoji: "🥘" },
    { nombre: "Wok de pollo con arroz basmati", calorias: 540, proteina: 42, carbos: 52, grasa: 16, emoji: "🥡" },
  ],
  snack: [
    { nombre: "Plátano con crema de cacahuete", calorias: 250, proteina: 8, carbos: 30, grasa: 12, emoji: "🍌" },
    { nombre: "Yogur griego con miel", calorias: 180, proteina: 15, carbos: 18, grasa: 6, emoji: "🍯" },
    { nombre: "Puñado de nueces y fruta", calorias: 220, proteina: 6, carbos: 20, grasa: 14, emoji: "🥜" },
    { nombre: "Batido de proteínas", calorias: 150, proteina: 25, carbos: 5, grasa: 3, emoji: "🥤" },
    { nombre: "Tostada con jamón serrano", calorias: 200, proteina: 14, carbos: 20, grasa: 8, emoji: "🥖" },
    { nombre: "Cottage cheese con piña", calorias: 170, proteina: 18, carbos: 15, grasa: 4, emoji: "🍍" },
  ],
  cena: [
    { nombre: "Merluza al vapor con verduras", calorias: 350, proteina: 35, carbos: 20, grasa: 12, emoji: "🐟" },
    { nombre: "Pechuga de pavo con ensalada grande", calorias: 320, proteina: 40, carbos: 15, grasa: 10, emoji: "🥗" },
    { nombre: "Tortilla francesa con espárragos", calorias: 280, proteina: 22, carbos: 8, grasa: 18, emoji: "🍳" },
    { nombre: "Salmón con calabacín a la plancha", calorias: 380, proteina: 35, carbos: 12, grasa: 22, emoji: "🥒" },
    { nombre: "Pollo al limón con champiñones", calorias: 340, proteina: 38, carbos: 10, grasa: 16, emoji: "🍋" },
    { nombre: "Sepia a la plancha con pimientos", calorias: 300, proteina: 32, carbos: 12, grasa: 12, emoji: "🦑" },
  ]
};

const NutricionPage = () => {
  const [loading, setLoading] = useState(true);
  const [acceso, setAcceso] = useState(null);
  const [pildoraActual, setPildoraActual] = useState(null);
  const [totalPildoras, setTotalPildoras] = useState(0);
  const [completando, setCompletando] = useState(false);
  const [macros, setMacros] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [menuDia, setMenuDia] = useState(null);
  const [activeTab, setActiveTab] = useState('curso');
  
  // Form para calculadora
  const [formCalc, setFormCalc] = useState({
    peso: '',
    altura: '',
    edad: '',
    sexo: 'hombre',
    actividad: 'moderada',
    objetivo: 'mantener',
    grasa_corporal: '',
    grasa_visceral: '',
    masa_muscular: ''
  });

  useEffect(() => {
    checkAcceso();
  }, []);

  const checkAcceso = async () => {
    try {
      const response = await axios.get(`${API}/me/nutricion/acceso`);
      setAcceso(response.data);
      
      if (response.data.tiene_acceso) {
        fetchPildoraActual();
        fetchMacros();
      }
    } catch (error) {
      console.error('Error checking acceso:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPildoraActual = async () => {
    try {
      const response = await axios.get(`${API}/me/nutricion/pildora-actual`);
      setPildoraActual(response.data.pildora);
      setTotalPildoras(response.data.progreso?.total_pildoras || response.data.total || 10);
      
      if (response.data.progreso?.herramientas_desbloqueadas) {
        setActiveTab('plan');
      }
    } catch (error) {
      console.error('Error fetching pildora:', error);
    }
  };

  const fetchMacros = async () => {
    try {
      const response = await axios.get(`${API}/me/nutricion/mis-macros`);
      if (response.data.tiene_macros) {
        setMacros(response.data.macros);
        generarMenuDia(response.data.macros);
      }
    } catch (error) {
      console.error('Error fetching macros:', error);
    }
  };

  const completarPildora = async () => {
    setCompletando(true);
    try {
      const response = await axios.post(`${API}/me/nutricion/completar-pildora`);
      toast.success('¡Lección completada! 🎉');
      
      if (response.data.herramientas_desbloqueadas) {
        toast.success('🔓 ¡Has desbloqueado el Generador de Menús!', { duration: 5000 });
        setActiveTab('plan');
      }
      
      fetchPildoraActual();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar');
    } finally {
      setCompletando(false);
    }
  };

  const calcularMacros = async () => {
    if (!formCalc.peso || !formCalc.altura || !formCalc.edad) {
      toast.error('Completa todos los campos');
      return;
    }

    setCalculando(true);
    try {
      const response = await axios.post(`${API}/me/nutricion/calcular-macros`, {
        peso: parseFloat(formCalc.peso),
        altura: parseFloat(formCalc.altura),
        edad: parseInt(formCalc.edad),
        sexo: formCalc.sexo,
        nivel_actividad: formCalc.actividad,
        objetivo: formCalc.objetivo
      });
      
      setMacros(response.data);
      toast.success('¡Macros calculados! Generando tu menú...');
      generarMenuDia(response.data);
    } catch (error) {
      toast.error('Error al calcular macros');
    } finally {
      setCalculando(false);
    }
  };

  const generarMenuDia = (macrosData) => {
    const calorias = macrosData.calorias_objetivo;
    
    // Distribución: Desayuno 25%, Comida 35%, Snack 15%, Cena 25%
    const distribucion = {
      desayuno: calorias * 0.25,
      comida: calorias * 0.35,
      snack: calorias * 0.15,
      cena: calorias * 0.25
    };

    const menu = {};
    Object.keys(distribucion).forEach(tipo => {
      const calTarget = distribucion[tipo];
      const opciones = BANCO_COMIDAS[tipo];
      
      // Ordenar por cercanía al target y tomar las 3 mejores
      const ordenadas = [...opciones].sort((a, b) => 
        Math.abs(a.calorias - calTarget) - Math.abs(b.calorias - calTarget)
      );
      
      menu[tipo] = {
        principal: ordenadas[0],
        alternativas: [ordenadas[1], ordenadas[2]],
        seleccionada: 0
      };
    });

    setMenuDia(menu);
  };

  const cambiarComida = (tipo) => {
    if (!menuDia) return;
    
    const actual = menuDia[tipo];
    const nuevaSeleccion = (actual.seleccionada + 1) % 3;
    
    const todasOpciones = [actual.principal, ...actual.alternativas];
    
    setMenuDia({
      ...menuDia,
      [tipo]: {
        ...actual,
        seleccionada: nuevaSeleccion,
        principal: todasOpciones[nuevaSeleccion],
        alternativas: todasOpciones.filter((_, i) => i !== nuevaSeleccion)
      }
    });
    
    toast.success('Comida cambiada');
  };

  const getTotalMenuDia = () => {
    if (!menuDia) return { calorias: 0, proteina: 0, carbos: 0, grasa: 0 };
    
    return Object.values(menuDia).reduce((acc, comida) => ({
      calorias: acc.calorias + comida.principal.calorias,
      proteina: acc.proteina + comida.principal.proteina,
      carbos: acc.carbos + comida.principal.carbos,
      grasa: acc.grasa + comida.principal.grasa
    }), { calorias: 0, proteina: 0, carbos: 0, grasa: 0 });
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ClientLayout>
    );
  }

  // Sin acceso
  if (!acceso?.tiene_acceso) {
    return (
      <ClientLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="bg-card border-border overflow-hidden">
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 p-8 text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Apple className="h-10 w-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Módulo de Nutrición</h1>
              <p className="text-muted-foreground">Aprende a comer bien y genera tu plan personalizado</p>
            </div>
            
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">5 lecciones prácticas</p>
                    <p className="text-sm text-muted-foreground">Aprende los fundamentos sin teoría innecesaria</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Calculadora de macros</p>
                    <p className="text-sm text-muted-foreground">Personalizada según tu objetivo</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Menús diarios con alternativas</p>
                    <p className="text-sm text-muted-foreground">Cambia las comidas según te apetezca</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">Contacta con tu entrenador para activar este módulo</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </ClientLayout>
    );
  }

  const progresoCurso = pildoraActual ? ((pildoraActual.numero_orden - 1) / totalPildoras) * 100 : 0;
  const cursoCompletado = acceso?.progreso?.herramientas_desbloqueadas;

  return (
    <ClientLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header con progreso */}
        <Card className="bg-card border-border overflow-hidden">
          <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Apple className="h-8 w-8 text-green-400" />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Módulo de Nutrición</h1>
                  <p className="text-sm text-muted-foreground">
                    {cursoCompletado ? '✅ Curso completado' : `Lección ${pildoraActual?.numero_orden || 1} de ${totalPildoras}`}
                  </p>
                </div>
              </div>
              {cursoCompletado && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">
                  <Trophy className="h-4 w-4" />
                  Completado
                </div>
              )}
            </div>
            <Progress value={cursoCompletado ? 100 : progresoCurso} className="h-2" />
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 bg-card border border-border">
            <TabsTrigger value="curso" className="data-[state=active]:bg-green-500/20">
              📚 Curso
            </TabsTrigger>
            <TabsTrigger 
              value="plan" 
              disabled={!cursoCompletado}
              className="data-[state=active]:bg-emerald-500/20"
            >
              🍽️ Mi Plan {!cursoCompletado && '🔒'}
            </TabsTrigger>
          </TabsList>

          {/* TAB CURSO */}
          <TabsContent value="curso" className="space-y-4">
            {pildoraActual && !cursoCompletado ? (
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center text-green-400 font-bold text-xl">
                      {pildoraActual.numero_orden}
                    </div>
                    <div>
                      <span className="text-xs text-green-400 font-medium uppercase">{pildoraActual.categoria}</span>
                      <CardTitle className="text-lg">{pildoraActual.titulo}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="prose prose-invert prose-sm max-w-none">
                    {pildoraActual.contenido.split('\n\n').map((p, i) => (
                      <p key={i} className="text-foreground/90 mb-4 leading-relaxed whitespace-pre-line">
                        {p.split('**').map((part, j) => 
                          j % 2 === 1 ? <strong key={j} className="text-green-300">{part}</strong> : part
                        )}
                      </p>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={completarPildora}
                    disabled={completando}
                    className="w-full bg-green-600 hover:bg-green-700 h-12"
                  >
                    {completando ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Completar y continuar
                        <ChevronRight className="h-5 w-5 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : cursoCompletado ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trophy className="h-10 w-10 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">¡Curso Completado!</h2>
                  <p className="text-muted-foreground mb-6">
                    Ya tienes los conocimientos. Ahora ve a "Mi Plan" para generar tu menú personalizado.
                  </p>
                  <Button 
                    onClick={() => setActiveTab('plan')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Ir a Mi Plan
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : null}
          </TabsContent>

          {/* TAB MI PLAN */}
          <TabsContent value="plan" className="space-y-4">
            {!macros ? (
              /* Calculadora de macros */
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-emerald-400" />
                    Calculadora de Macros
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Introduce tus datos para calcular tus necesidades nutricionales personalizadas.
                  </p>
                  
                  {/* Datos básicos */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-emerald-400 uppercase">Datos básicos</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Peso (kg)</Label>
                        <Input
                          type="number"
                          value={formCalc.peso}
                          onChange={(e) => setFormCalc({...formCalc, peso: e.target.value})}
                          placeholder="75"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Altura (cm)</Label>
                        <Input
                          type="number"
                          value={formCalc.altura}
                          onChange={(e) => setFormCalc({...formCalc, altura: e.target.value})}
                          placeholder="175"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Edad</Label>
                        <Input
                          type="number"
                          value={formCalc.edad}
                          onChange={(e) => setFormCalc({...formCalc, edad: e.target.value})}
                          placeholder="30"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Sexo</Label>
                        <Select value={formCalc.sexo} onValueChange={(v) => setFormCalc({...formCalc, sexo: v})}>
                          <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hombre">Hombre</SelectItem>
                            <SelectItem value="mujer">Mujer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Composición corporal */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-blue-400 uppercase">Composición corporal</p>
                      <span className="text-xs text-muted-foreground">📊 Datos de tu báscula inteligente</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Scale className="h-3 w-3" />
                          Grasa corporal (%)
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formCalc.grasa_corporal}
                          onChange={(e) => setFormCalc({...formCalc, grasa_corporal: e.target.value})}
                          placeholder="18.5"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Grasa visceral
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formCalc.grasa_visceral}
                          onChange={(e) => setFormCalc({...formCalc, grasa_visceral: e.target.value})}
                          placeholder="8"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Dumbbell className="h-3 w-3" />
                          Masa muscular (%)
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formCalc.masa_muscular}
                          onChange={(e) => setFormCalc({...formCalc, masa_muscular: e.target.value})}
                          placeholder="42"
                          className="bg-zinc-900/50 border-zinc-800"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      💡 ¿No tienes estos datos? Pregunta a tu entrenador por una báscula de bioimpedancia
                    </p>
                  </div>

                  {/* Actividad y objetivo */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-amber-400 uppercase">Actividad y objetivo</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nivel de actividad</Label>
                        <Select value={formCalc.actividad} onValueChange={(v) => setFormCalc({...formCalc, actividad: v})}>
                          <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sedentario">Sedentario</SelectItem>
                            <SelectItem value="ligera">Actividad ligera</SelectItem>
                            <SelectItem value="moderada">Moderada (3-4 días gym)</SelectItem>
                            <SelectItem value="activa">Muy activa (5+ días)</SelectItem>
                            <SelectItem value="muy_activa">Atleta profesional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Objetivo</Label>
                        <Select value={formCalc.objetivo} onValueChange={(v) => setFormCalc({...formCalc, objetivo: v})}>
                          <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="perder">Perder grasa</SelectItem>
                            <SelectItem value="mantener">Mantener peso</SelectItem>
                            <SelectItem value="ganar">Ganar músculo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={calcularMacros}
                    disabled={calculando}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                  >
                    {calculando ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5 mr-2" />
                        Calcular y generar menú
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Menú del día */
              <div className="space-y-4">
                {/* Resumen de macros */}
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <Target className="h-5 w-5 text-emerald-400" />
                        Tu Objetivo Diario
                      </h3>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setMacros(null)}
                        className="text-xs"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Recalcular
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <Flame className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{macros.calorias_objetivo}</p>
                        <p className="text-xs text-muted-foreground">kcal</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <div className="w-5 h-5 rounded bg-red-400 mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{macros.proteina}g</p>
                        <p className="text-xs text-muted-foreground">Proteína</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <div className="w-5 h-5 rounded bg-amber-400 mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{macros.carbohidratos}g</p>
                        <p className="text-xs text-muted-foreground">Carbos</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <div className="w-5 h-5 rounded bg-blue-400 mx-auto mb-1" />
                        <p className="text-xl font-bold text-foreground">{macros.grasa}g</p>
                        <p className="text-xs text-muted-foreground">Grasa</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Menú del día */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Utensils className="h-5 w-5 text-emerald-400" />
                      Tu Menú de Hoy
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {menuDia && Object.entries(menuDia).map(([tipo, datos]) => {
                      const icons = {
                        desayuno: <Coffee className="h-5 w-5" />,
                        comida: <Sun className="h-5 w-5" />,
                        snack: <Cookie className="h-5 w-5" />,
                        cena: <Moon className="h-5 w-5" />
                      };
                      const labels = {
                        desayuno: 'Desayuno',
                        comida: 'Comida',
                        snack: 'Snack',
                        cena: 'Cena'
                      };
                      
                      return (
                        <div 
                          key={tipo} 
                          className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700 hover:border-emerald-500/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400">
                                {icons[tipo]}
                              </div>
                              <div>
                                <p className="text-xs text-emerald-400 font-medium uppercase mb-1">{labels[tipo]}</p>
                                <p className="font-medium text-foreground">
                                  {datos.principal.emoji} {datos.principal.nombre}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {datos.principal.calorias} kcal · P: {datos.principal.proteina}g · C: {datos.principal.carbos}g · G: {datos.principal.grasa}g
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => cambiarComida(tipo)}
                              className="text-emerald-400 hover:text-emerald-300"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Alternativas */}
                          <div className="mt-3 pt-3 border-t border-zinc-700">
                            <p className="text-xs text-muted-foreground mb-2">Alternativas:</p>
                            <div className="flex flex-wrap gap-2">
                              {datos.alternativas.map((alt, i) => (
                                <span key={i} className="text-xs bg-zinc-700/50 px-2 py-1 rounded">
                                  {alt.emoji} {alt.nombre.substring(0, 25)}...
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Total del día */}
                    {menuDia && (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mt-4">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-foreground">Total del día</p>
                          <div className="flex gap-4 text-sm">
                            <span className="text-orange-400">{getTotalMenuDia().calorias} kcal</span>
                            <span className="text-red-400">P: {getTotalMenuDia().proteina}g</span>
                            <span className="text-amber-400">C: {getTotalMenuDia().carbos}g</span>
                            <span className="text-blue-400">G: {getTotalMenuDia().grasa}g</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </ClientLayout>
  );
};

export default NutricionPage;
